/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { AfterViewInit, Component } from '@angular/core';
import { BaseComponent } from '../base.component';
import { environment } from '../../../../environments/environment';
import { hits, searchBox } from 'instantsearch.js/es/widgets';
import { LanguageService } from '../../services';

@Component({
    selector: 'app-algolia-search',
    templateUrl: './algolia-search.component.html',
    styleUrls: ['./algolia-search.component.scss']
})
export class AlgoliaSearchComponent extends BaseComponent implements AfterViewInit {
    /**
     * Store the environment, so we can decide whether to display the search on the left or right on the input.
     */
    public environmentClass: string = this.modulesEnum[this.environment.module].toLowerCase();

    constructor(
        private languageService: LanguageService,
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.setUpSearch();
    }

    /**
     * Set up the search object.
     * @private
     */
    private setUpSearch(): void {
        const algoliaClient = algoliasearch(
            'EK04BNHENS',
            'fb66564ef6d7bcbfc4b8fbeb690c1e03'
        );

        const search = instantsearch({
            indexName: (environment.production ? 'portal_lborg' : 'dev_portal_lborg'),
            searchClient: algoliaClient,
            future: {
                preserveSharedStateOnUnmount: true,
            },
            onStateChange({ uiState, setUiState }) {
                search.addWidgets([
                    hits({
                        transformItems(items, { results }) {
                            if (results.query === '') {
                                return [];
                            }

                            return items;
                        },
                        container: '#hits',
                        templates: {
                            empty(results: { query: string; }, { html }: any) {
                                if (results.query === '') {
                                    return null;
                                }

                                return html`No results found for "${results.query}"`;
                            },
                            item(data: any, { html }: any) {
                                const showImage: boolean = data?.image;

                                return html`
                                    <article class="${data.title && data.excerpt && data.content ? 'show' : 'hide'}">
                                        <a
                                            class="flex-wrap hits-${data.search_type}" href="${data.url}"
                                            target="_blank">
                                            <div class="content-space-between">
                                                <div class="article-content-left">
                                                    <span class="title routerLink">${data.title}</span>
                                                    <span class="content">${data.excerpt}</span>
                                                    <span class="tags">
                                                        <span class="${data.search_type} tag-name">
                                                            ${data.search_type}
                                                        </span>
                                                        <span class="${data.tags ? 'show' : 'hide'}">
                                                            <span class="bullet">•</span>
                                                            <span class="tag-name">${data.tags}</span>
                                                        </span>
                                                        <span class="${data.author ? 'show' : 'hide'}">
                                                            <span class="bullet">•</span>
                                                            <span class="tag-name">${data.author}</span>
                                                        </span>
                                                   </span>
                                                </div>
                                                <div class="article-content-right">
                                                    <div class="image-wrapper ${showImage ? 'show' : 'hide'}">
                                                        <img src="${data.image}" alt="article image"/>
                                                    </div>
                                                    <div class="image-wrapper ${showImage ? 'hide' : 'show'}">
                                                        <div class="empty-image"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    </article>
                                `;
                            },
                        }
                    })
                ]);

                setUiState(uiState);
            },
        });

        search.addWidgets([
            searchBox({
                container: '#search-box-header',
                placeholder: `${this.languageService.translate('search_placeholder')}`,
                autofocus: false,
                showReset: true,
                showSubmit: false,
            }),
        ]);

        search.start();
    }
}

